/**
 * model — the view model of a proof page. The page is general: a product's source fixes the pictures
 * (`ProofContent`), the spec fixes the words and the order of the sections, and `buildProofPage` zips the two. No
 * read of the viewer is made here or anywhere in this folder.
 *
 * A section is shown when the content holds its visual. A source that has nothing for a spec'd section leaves it
 * out here, and each product's spec test holds that its source fills every section it declares.
 */
import type {DemoKind, ProofSpec, ProofView} from './spec'
import {viewOf} from './spec'

export const SHEET_SIZE = 18
export const CATALOGUE_MOSAIC_SIZE = 12
export const SEARCH_RESULTS_MAX = 6

/**
 * `w`/`h` are the picture's pixel size where it is not the usual 2:3, so a sheet can lay mixed shapes out. `to` is the
 * page of the site the picture opens, when it stands for one.
 */
export type Photo = {src: string; alt: string; w?: number; h?: number; to?: string}

/**
 * A grid of whole rows at each width: `photos` holds `cols × rows` pictures, the page shows the first
 * `colsNarrow × rowsNarrow` on a phone and hides the rest by CSS, so their lazy images are never fetched.
 */
export type Rows = {cols: number; rows: number; colsNarrow: number; rowsNarrow: number}

/** The number of pictures a `Rows` grid holds on a desktop. */
export const rowsSize = ({cols, rows}: Rows) => cols * rows

export type FacePair = {from: Photo; withYou: Photo}

/** A picture of the product's own wall or catalogue, named by its pack. */
export type Sample = {slug: string; src: string}

export type EditPair = {before: Photo; after: Photo; direction: string}

/** One section's pictures, by kind. */
export type Visual =
    | {kind: 'catalogue'; photos: Photo[]; rows?: Rows}
    | {kind: 'search'; query: string; results: Photo[]; rows?: Rows}
    | {kind: 'pair'; pairs: FacePair[]}
    | {kind: 'edit'; pairs: EditPair[]}
    | {kind: 'scratch'; direction: string; photo: Photo}
    | {kind: 'blank'; photo: Photo}
    | {kind: 'themes'; items: Array<{label: string; packs: number; photo: Photo}>; totals: {packs: number; pictures: number}}
    | {kind: 'neighbours'; picture: Photo; neighbours: Photo[]}
    | {
          kind: 'muse'
          /** Muses, one row each: the same woman in different scenes. */
          muses: Array<{name: string; scenes: Photo[]}>
      }
    | {
          kind: 'ownMuse'
          /** The made-up person whose photos go in, and her in scenes once she is ready. */
          name: string
          photosIn: Photo[]
          /** The product's own in-progress line for her, in its words, and the picture it shows beside it. */
          building: {line: string; percent: number; thumb: Photo}
          her: Photo[]
      }
    | {kind: 'sessions'; sessions: SessionCard[]}
    | {kind: 'listen'; sessions: SessionCard[]}
    /** `ref` names which of the product's own screens to draw; `steps` are their words, in order. */
    | {kind: 'steps'; ref: string; steps: Array<{title: string; body: string}>}
    | {kind: 'note'; lines: string[]}
    | {kind: 'links'; items: Array<{label: string; body: string; to: string}>}

/** A session of the product's audio, as the page plays it. `lang` is the language it is spoken in (BCP 47). */
export type SessionCard = {
    id: string
    title: string
    writtenFor: string
    lang: string
    /** The language's name in itself, shown where sessions of several languages stand together. */
    langName: string
    seconds: number
    src: string
    /** The words spoken, one entry per paragraph. */
    transcript: string[]
}

export type Chip = {label: string; slug: string; photo: Photo}

/** What a product's source fixes for the page. */
export type ProofContent = {
    sheet: Photo[]
    /** What stands in the hero in place of the sheet, for a product whose lead is not a picture. */
    hero?: Extract<Visual, {kind: 'sessions'}>
    visuals: Visual[]
    chips: Chip[]
}

export type Section = {kind: DemoKind; title: string; body: string | null; steps: readonly string[]; visual: Visual}

export type ProofData = {
    sheet: Photo[]
    hero: Extract<Visual, {kind: 'sessions'}> | null
    sections: Section[]
    chips: Chip[]
    view: ProofView
}

export function buildProofPage(spec: ProofSpec, content: ProofContent): ProofData {
    const sections = spec.demos.flatMap(({kind, title, body, steps}): Section[] => {
        const visual = content.visuals.find((v) => v.kind === kind)
        return visual ? [{kind, title, body: body ?? null, steps: steps ?? [], visual}] : []
    })
    return {sheet: content.sheet, hero: content.hero ?? null, sections, chips: content.chips, view: viewOf(spec)}
}

/** The visual of a kind in a page's data, narrowed. */
export function visualOf<K extends DemoKind>(data: ProofData, kind: K): Extract<Visual, {kind: K}> | null {
    return (data.sections.find((s) => s.kind === kind)?.visual as Extract<Visual, {kind: K}> | undefined) ?? null
}

/** Samples by slug, in the order asked; a slug the product does not hold is skipped. */
export function named(samples: readonly Sample[], slugs: readonly string[]): Sample[] {
    const bySlug = new Map(samples.map((s) => [s.slug, s]))
    return slugs.map((s) => bySlug.get(s)).filter((s): s is Sample => !!s)
}

export function chipsOf(spec: ProofSpec, samples: readonly Sample[], alt: string): Chip[] {
    const bySlug = new Map(samples.map((s) => [s.slug, s]))
    return spec.chips.flatMap(({label, slug}) => {
        const pack = bySlug.get(slug)
        return pack ? [{label, slug, photo: {src: pack.src, alt}}] : []
    })
}
